import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { MapPin, Users, ArrowRight, Zap } from "lucide-react";

/**
 * FOMO widget — "127 personnes attendent un artisan à Nantes"
 *
 * Displays top prospected cities where demandeurs are waiting for artisans.
 * Aimed at artisans (motivate sign-up) but useful anywhere on the site.
 *
 * Props:
 * - variant: "landing" (dark, hero-integrated) | "pro-page" (light, boxed) | "compact" (single ticker line)
 * - limit: number of top cities to show (default 6)
 */
export default function WaitingProspectsWidget({ variant = "pro-page", limit = 6 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get("/public/city-prospect-stats")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || data.total_prospects === 0) return null;
  const cities = (data.top_cities || []).slice(0, limit);
  if (cities.length === 0) return null;

  if (variant === "compact") {
    const first = cities[0];
    return (
      <div
        className="inline-flex items-center gap-2 border-2 border-[#FF4D00] bg-[#FF4D00]/10 px-3 py-1.5 mono text-[11px] uppercase tracking-widest"
        data-testid="waiting-prospects-compact"
      >
        <Zap className="w-3.5 h-3.5 text-[#FF4D00]" strokeWidth={2.5} />
        <span className="text-[#FF4D00] font-semibold">
          {first.unique_prospects} personnes attendent un artisan à {first.city_label}
        </span>
      </div>
    );
  }

  const isDark = variant === "landing";
  const bg = isDark ? "bg-[#0A0A0B] text-white" : "bg-white text-[#0A0A0B]";
  const cardBorder = isDark ? "border-white/20" : "border-[#0A0A0B]";

  return (
    <section
      className={`${bg} border-b-2 border-[#0A0A0B] relative overflow-hidden`}
      data-testid="waiting-prospects-widget"
    >
      {isDark && <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />}
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">
              // Signal du marché · live
            </div>
            <h2 className={`display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95] ${isDark ? "text-white" : ""}`}>
              <span className="text-[#FF4D00]">{data.total_prospects}</span> personnes attendent<br />
              un artisan Batisur en France.
            </h2>
            <p className={`mt-4 text-base max-w-2xl ${isDark ? "text-zinc-300" : "text-zinc-700"}`}>
              Réparties sur <strong className={isDark ? "text-white" : "text-[#0A0A0B]"}>{data.total_cities} villes prospectées</strong>. Chaque prospect a explicitement demandé
              qu'on le notifie dès qu'un artisan SIRET vérifié sera disponible dans son secteur.
            </p>
          </div>
          <Link to="/register?role=artisan&trial=1" data-testid="waiting-prospects-cta">
            <button className="btn-batisur flex items-center gap-2">
              Devenir artisan Batisur <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="waiting-prospects-cities">
          {cities.map((c, i) => (
            <Link
              key={c.city_slug}
              to={`/artisans/ville/${c.city_slug}`}
              className={`border-2 ${cardBorder} p-4 flex items-center justify-between transition group ${
                isDark
                  ? "bg-white/5 hover:bg-white/10 backdrop-blur"
                  : "bg-white hover:bg-[#FF4D00] hover:text-white shadow-[3px_3px_0_#0A0A0B]"
              }`}
              data-testid={`waiting-prospects-city-${c.city_slug}`}
            >
              <div>
                <div className="mono text-[10px] uppercase tracking-widest opacity-70">
                  // Ville #{i + 1}
                </div>
                <div className="display text-2xl mt-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#FF4D00]" strokeWidth={2.5} />
                  {c.city_label}
                </div>
                <div className="text-xs mt-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  <span className="font-semibold">{c.unique_prospects}</span> personnes attendent
                  {c.top_metier && (
                    <span className="mono text-[10px] uppercase tracking-widest opacity-70 ml-1">
                      · {c.top_metier}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <p className={`mt-6 text-xs ${isDark ? "text-zinc-500" : "text-zinc-500"}`}>
          Chiffres mis à jour en temps réel. Un artisan qui rejoint Batisur sur ces villes reçoit ses premiers contacts sous 48h en moyenne.
        </p>
      </div>
    </section>
  );
}
